import React, { useState, useEffect } from "react";
import { MediaItem } from "../../../../types/item";
import { getImage, getThumbnail } from "../../../../db";
import { ImSpinner2 } from "react-icons/im";
import { FaPlayCircle } from "react-icons/fa";
import { MediaType } from "../../../../types/enums/item";

interface MediaThumbnailProps {
  mediaItem: MediaItem;
  onError: () => void;
  className?: string;
}

const MediaThumbnail: React.FC<MediaThumbnailProps> = ({
  mediaItem,
  onError,
  className = "w-24 h-24 object-cover",
}) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Try to load from IndexedDB (local blob) - used when thumbnailUrl missing or failed
  const loadFromIndexedDB = async (): Promise<string | null> => {
    if (!mediaItem.uuid) return null;
    try {
      if (mediaItem.type_ID === MediaType.Video) {
        const thumb = await getThumbnail(mediaItem.uuid);
        if (thumb?.blob) return URL.createObjectURL(thumb.blob);
        const video = await getImage(mediaItem.uuid);
        if (video?.blob) return URL.createObjectURL(video.blob);
      } else {
        const imageResult = await getImage(mediaItem.uuid);
        if (imageResult?.blob) return URL.createObjectURL(imageResult.blob);
      }
    } catch (e) {
      console.warn(`Failed to load from IndexedDB for ${mediaItem.uuid}`, e);
    }
    return null;
  };

  useEffect(() => {
    const loadThumbnail = async () => {
      setLoading(true);
      setError(false);
      try {
        // 1) Use thumbnailUrl from backend only when present (do not fall back to file URL)
        if (mediaItem.thumbnailUrl) {
          setThumbnailUrl(mediaItem.thumbnailUrl);
          setLoading(false);
          return;
        }
        // 2) No thumbnail from backend: use locally stored media (IndexedDB)
        const blobUrl = await loadFromIndexedDB();
        if (blobUrl) {
          setThumbnailUrl(blobUrl);
        } else if (mediaItem.url) {
          // 3) Fall back to file URL (inspection blob is kept, so this URL is valid)
          setThumbnailUrl(mediaItem.url);
        } else {
          // 4) Nothing available: show gray media icon
          setError(true);
          onError();
        }
        setLoading(false);
      } catch (e) {
        console.warn(`Failed to load thumbnail for ${mediaItem.uuid}`, e);
        setError(true);
        onError();
        setLoading(false);
      }
    };

    loadThumbnail();

    // Cleanup object URLs to prevent memory leaks
    return () => {
      if (thumbnailUrl && thumbnailUrl.startsWith("blob:")) {
        URL.revokeObjectURL(thumbnailUrl);
      }
    };
  }, [mediaItem.uuid, mediaItem.thumbnailUrl]);

  if (loading) {
    return (
      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
        <ImSpinner2 className="animate-spin text-gray-400" />
      </div>
    );
  }
  if (!thumbnailUrl && mediaItem.type_ID === MediaType.Video) {
    return (
      <div className="w-full h-full bg-gray-100 flex flex-col items-center justify-center p-2">
        <FaPlayCircle className="text-white text-5xl drop-shadow-lg opacity-90" />
      </div>
    );
  }

  if (error) {
    // Show media icon placeholder instead of error text - thumbnail may still be processing
    return (
   <div className="w-full h-full bg-gray-100 flex flex-col items-center justify-center p-2">
        <svg
          className="w-12 h-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </div>   
    );
  }

  return (
    <>
      <img
        src={thumbnailUrl}
        alt="Media"
        className={className}
        onError={async () => {
          // URL failed to load: try locally stored media, then file URL, then gray icon
          const blobUrl = await loadFromIndexedDB();
          if (blobUrl) {
            setThumbnailUrl(blobUrl);
            setError(false);
          } else if (mediaItem.url && thumbnailUrl !== mediaItem.url) {
            setThumbnailUrl(mediaItem.url);
            setError(false);
          } else {
            setError(true);
            onError();
          }
        }}
      />
      {mediaItem.type_ID === MediaType.Video && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <FaPlayCircle className="text-white text-7xl drop-shadow-lg opacity-90" />
        </div>
      )}
    </>
  );
};

export default MediaThumbnail;
